// src/components/public_utilities/assetAiGuide.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Asset AI guide (pure JS, no React)
//
// Explains what information FlowSense needs for every asset type, which AI/ML
// features use it, and turns an asset record into:
//   • a weighted data-readiness score + per-field checklist
//   • per-model readiness (ready / partial / blocked)
//   • plain-language insights (design life, maintenance, surge exposure, …)
//   • a health index with the drivers that explain it and recommended actions
//   • a network roll-up for dashboards
//
// Field names differ between forms (install_date vs installation_date,
// model_number vs model), so everything goes through normalizeAsset().
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Asset families — what kind of infrastructure the free-text `type` describes
// ─────────────────────────────────────────────────────────────────────────────
export const ASSET_FAMILIES = Object.freeze({
    well: { label: 'Well / borehole', lifespan: 30, hydraulic: true, sensors: true },
    basin: { label: 'Catch basin / drain', lifespan: 40, hydraulic: true, sensors: false },
    pump: { label: 'Pump / motor', lifespan: 15, hydraulic: true, sensors: true },
    hydrant: { label: 'Fire hydrant', lifespan: 50, hydraulic: true, sensors: false },
    valve: { label: 'Valve', lifespan: 35, hydraulic: true, sensors: false },
    pipe: { label: 'Pipe / main', lifespan: 60, hydraulic: true, sensors: false },
    tank: { label: 'Tank / reservoir', lifespan: 40, hydraulic: true, sensors: true },
    meter: { label: 'Meter / sensor / controller', lifespan: 15, hydraulic: false, sensors: true },
    electrical: { label: 'Electrical / lighting', lifespan: 20, hydraulic: false, sensors: false },
    road: { label: 'Road', lifespan: 18, hydraulic: false, sensors: false },
    bridge: { label: 'Bridge / structure', lifespan: 75, hydraulic: false, sensors: false },
    other: { label: 'Other asset', lifespan: 20, hydraulic: false, sensors: false },
});

const FAMILY_PATTERNS = [
    ['well', /\b(well|borehole|bore)\b/],
    ['basin', /(basin|inlet|manhole|culvert|outfall|drain|soakaway|gully)/],
    ['pump', /(pump|lift station|motor|compressor|blower)/],
    ['hydrant', /hydrant/],
    ['valve', /valve/],
    ['pipe', /(pipe|main|pipeline|sewer|conduit)/],
    ['tank', /(tank|reservoir|cistern)/],
    ['meter', /(meter|sensor|controller|logger|scada|rtu)/],
    ['electrical', /(light|signal|generator|transformer|turbine|panel|switchgear|hvac|boiler)/],
    ['bridge', /(bridge|culvert bridge|overpass|structure)/],
    ['road', /(road|street|pavement|highway)/],
];

export const getAssetFamily = (type) => {
    const t = String(type || '').toLowerCase();
    for (const [key, re] of FAMILY_PATTERNS) if (re.test(t)) return key;
    return 'other';
};

// ─────────────────────────────────────────────────────────────────────────────
// AI / ML features that consume asset data
// ─────────────────────────────────────────────────────────────────────────────
export const ASSET_AI_MODELS = Object.freeze({
    failure: {
        label: 'Failure prediction', short: 'Failure ML', color: 'purple',
        explain: 'Predicts which assets are likely to fail and when, so crews can fix them first. It learns from age, design life, maintenance history, make/model and criticality. Its scores appear in Predictive Maintenance.',
    },
    anomaly: {
        label: 'Sensor anomaly detection', short: 'Anomaly AI', color: 'orange',
        explain: 'An Isolation Forest model watches live sensor readings (pressure, level, flow) and flags unusual behaviour before it becomes a failure. It only works for assets with linked sensors.',
    },
    hydraulic: {
        label: 'Flood & hydraulic model', short: 'Hydraulics', color: 'blue',
        explain: 'Simulates flooding, well surcharge and storm surge. It needs the asset\'s position, surveyed elevation and (for wells and basins) the design depth and rim height.',
    },
    lifecycle: {
        label: 'Repair-vs-replace & lifecycle cost', short: 'Lifecycle', color: 'green',
        explain: 'Compares the cost of continuing repairs with the cost of replacement, using purchase cost, age, design life and logged maintenance costs.',
    },
    gis: {
        label: 'GIS map & Digital Twin', short: 'GIS / Twin', color: 'teal',
        explain: 'Places the asset on the map and in the 3D Digital Twin so it can be found, linked to nearby sensors and included in cascade simulations.',
    },
    dispatch: {
        label: 'Alert priority & crew dispatch', short: 'Dispatch', color: 'red',
        explain: 'Ranks alerts and routes work orders. Criticality, the responsible team and a clear location description decide how fast the right crew arrives.',
    },
});

const BAHAMAS = { minLat: 20.5, maxLat: 27.5, minLng: -80.5, maxLng: -72.0 };
const SEA_LEVEL_RISE_M = 0.23;
const DAY_MS = 86400000;
const YEAR_MS = 365.25 * DAY_MS;
const HYDRAULIC = Object.keys(ASSET_FAMILIES).filter(k => ASSET_FAMILIES[k].hydraulic);
const DEPTH_FAMILIES = ['well', 'basin', 'pump'];
const RIM_FAMILIES = ['well', 'basin'];
const SENSOR_FAMILIES = Object.keys(ASSET_FAMILIES).filter(k => ASSET_FAMILIES[k].sensors);
const ELEVATION_FAMILIES = [...HYDRAULIC, 'road', 'electrical', 'bridge'];

// ─────────────────────────────────────────────────────────────────────────────
// Field guide
//   level: required | recommended | optional  (may vary by family via levelFor)
//   form / edit: where the field can be filled in → { tab, id } or null
// ─────────────────────────────────────────────────────────────────────────────
export const ASSET_FIELD_GUIDE = Object.freeze({
    name: {
        label: 'Asset name / tag', level: 'required', weight: 3, usedBy: ['dispatch'],
        form: { tab: 0 }, edit: null,
        explain: 'The name crews and residents use for this asset.',
        why: 'Shown in every alert, work order and report — a clear name avoids sending crews to the wrong asset.',
        example: 'Well Station #04 – South Road',
    },
    type: {
        label: 'Asset type', level: 'required', weight: 3, usedBy: ['failure', 'hydraulic', 'lifecycle'],
        form: { tab: 0 }, edit: { tab: 0 },
        explain: 'What kind of infrastructure this is (well, catch basin, pump, hydrant…).',
        why: 'Every model behaves differently per type: wells use the hydraulic model, pumps use sensor anomaly detection, and each type has its own failure pattern and design life.',
        example: 'Catch Basin',
    },
    status: {
        label: 'Operational status', level: 'required', weight: 1, usedBy: ['dispatch'],
        form: { tab: 0 }, edit: { tab: 0 },
        explain: 'Whether the asset is working, needs maintenance, is damaged or retired.',
        why: 'Drives dashboards, the map colour and whether new alerts are escalated.',
        example: 'Operational',
    },
    asset_criticality: {
        label: 'Criticality', level: 'recommended', weight: 3, usedBy: ['failure', 'dispatch'],
        form: { tab: 0 }, edit: null,
        explain: 'How important the asset is — what happens to residents if it fails.',
        why: 'A failure on a critical asset (hospital supply, main pump) is ranked above the same failure on a minor one. Also weights the failure model\'s risk score.',
        example: 'High',
    },
    managed_by_team_id: {
        label: 'Responsible team', level: 'optional', weight: 1, usedBy: ['dispatch'],
        form: { tab: 0 }, edit: null,
        explain: 'The crew or department that maintains this asset.',
        why: 'AI-generated work orders are routed straight to this team instead of waiting in a shared queue.',
        example: 'Water Distribution – North',
    },
    location_description: {
        label: 'Location description', level: 'recommended', weight: 2, usedBy: ['dispatch'],
        form: { tab: 1 }, edit: { tab: 1 },
        explain: 'A landmark, street or intersection that helps a crew find the asset.',
        why: 'GPS can be several metres off; a description gets crews to the right manhole or valve box quickly.',
        example: 'East side of Baillou Hill Rd at Wulff Rd',
    },
    coordinates: {
        label: 'Location (lat / lng)', level: 'required', weight: 4, usedBy: ['gis', 'hydraulic'],
        fieldIds: ['latitude', 'longitude'], form: { tab: 1 }, edit: { tab: 1 },
        explain: 'Decimal-degree position. New Providence is about 25.0°N, −77.4°W (longitude is negative).',
        why: 'Puts the asset on the GIS map and Digital Twin, and lets the flood model and weather service use the right local conditions.',
        example: '25.0343, −77.3963',
    },
    elevation: {
        label: 'Surveyed elevation', unit: 'm', level: 'recommended', weight: 3, usedBy: ['hydraulic'],
        families: ELEVATION_FAMILIES, levelFor: (f) => (HYDRAULIC.includes(f) ? 'recommended' : 'optional'),
        form: { tab: 1 }, edit: { tab: 1 },
        explain: 'Ground or rim height above mean sea level, from a survey or LiDAR.',
        why: 'Flooding is about height: the hydraulic model compares water levels with this value to decide when the asset is under water or a well overflows. Without it a 2.5 m default is used.',
        example: '4.25',
    },
    base_depth_m: {
        label: 'Design / sump depth', unit: 'm', level: 'recommended', weight: 3, usedBy: ['hydraulic'],
        families: DEPTH_FAMILIES, metadata: true, form: { tab: 1 }, edit: { tab: 1 },
        explain: 'How deep the well, sump or wet-well is below its rim.',
        why: 'Sets the storage volume and how fast water rises during rain — the core of the well surcharge and siltation predictions.',
        example: '18.5',
    },
    rim_elevation_offset_m: {
        label: 'Rim height above grade', unit: 'm', level: 'optional', weight: 1, usedBy: ['hydraulic'],
        families: RIM_FAMILIES, metadata: true, form: { tab: 1 }, edit: null,
        explain: 'How far the rim or curb sits above the surrounding pavement.',
        why: 'Decides when water spills onto the road instead of into the structure (overtopping).',
        example: '0.15',
    },
    install_date: {
        label: 'Install / commission date', level: 'recommended', weight: 3, usedBy: ['failure', 'lifecycle'],
        form: { tab: 2 }, edit: { tab: 2, id: 'installation_date' },
        explain: 'When the asset went into service.',
        why: 'Age is the strongest single predictor of failure and the basis of remaining-life and replacement planning.',
        example: '2014-06-01',
    },
    expected_lifespan_years: {
        label: 'Design lifespan', unit: 'years', level: 'recommended', weight: 2, usedBy: ['failure', 'lifecycle'],
        form: { tab: 2 }, edit: null,
        explain: 'How many years the manufacturer or engineer expects it to last.',
        why: 'Combined with age it gives "life used" — assets past design life fail more often and are replacement candidates.',
        example: '15',
    },
    last_maintenance_date: {
        label: 'Last maintenance', level: 'recommended', weight: 3, usedBy: ['failure'],
        form: { tab: 2 }, edit: { tab: 2 },
        explain: 'When the asset was last serviced.',
        why: 'Overdue maintenance raises failure risk; the model also learns how quickly this asset degrades between services.',
        example: '2026-07-14',
    },
    manufacturer: {
        label: 'Manufacturer', level: 'optional', weight: 1, usedBy: ['failure'],
        form: { tab: 2 }, edit: { tab: 1 },
        explain: 'Who made the equipment.',
        why: 'Failure rates differ by make; the model groups similar equipment to learn from the whole fleet.',
        example: 'Grundfos',
    },
    model_number: {
        label: 'Model number', level: 'optional', weight: 1, usedBy: ['failure'],
        form: { tab: 2 }, edit: { tab: 1, id: 'model' },
        explain: 'The manufacturer\'s model or part number.',
        why: 'Lets FlowSense spot model-wide defects and stock the right spare parts.',
        example: 'CR 32-6-2',
    },
    serial_number: {
        label: 'Serial number', level: 'optional', weight: 1, usedBy: [],
        form: { tab: 2 }, edit: { tab: 1 },
        explain: 'The unique number on the equipment plate.',
        why: 'Needed for warranty claims and to tell identical units apart.',
        example: 'SN-2024-00142',
    },
    purchase_cost: {
        label: 'Purchase / capital cost', unit: '$', level: 'recommended', weight: 2, usedBy: ['lifecycle'],
        form: { tab: 2 }, edit: { tab: 0 },
        explain: 'What the asset cost to buy and install.',
        why: 'Repair-vs-replace compares accumulated repair costs with this value.',
        example: '18500',
    },
    warranty_expiration_date: {
        label: 'Warranty expiry', level: 'optional', weight: 1, usedBy: ['lifecycle'],
        form: { tab: 2 }, edit: null,
        explain: 'When the manufacturer warranty ends.',
        why: 'FlowSense flags failures that are still under warranty so repairs can be claimed.',
        example: '2027-01-31',
    },
    sensors: {
        label: 'Linked sensors', level: 'recommended', weight: 3, usedBy: ['anomaly'],
        families: SENSOR_FAMILIES, form: null, edit: null,
        explain: 'Live devices (pressure, level, flow) attached to this asset.',
        why: 'Sensor anomaly detection only works with live readings — it can warn hours or days before a pump or well fails.',
        example: 'WL-NP-042 water level',
    },
    parent_asset_id: {
        label: 'Parent facility', level: 'optional', weight: 1, usedBy: ['hydraulic', 'gis'],
        form: { tab: 3 }, edit: null,
        explain: 'The station or wellfield this component belongs to.',
        why: 'Lets simulations cascade a failure from a facility to its components (and back).',
        example: 'Wellfield-A',
    },
    qr_code_tag: {
        label: 'QR / barcode tag', level: 'optional', weight: 1, usedBy: ['dispatch'],
        form: { tab: 3 }, edit: null,
        explain: 'The physical tag crews scan with the mobile app.',
        why: 'Scanning confirms the crew is at the right asset and opens its history instantly.',
        example: 'QR-WS-0042',
    },
    model_url: {
        label: '3D model', level: 'optional', weight: 1, usedBy: ['gis'],
        form: { tab: 3 }, edit: null,
        explain: 'A .glb / .gltf file shown in the Digital Twin.',
        why: 'Optional — the Digital Twin uses a generic shape when missing.',
        example: 'https://…/pump.glb',
    },
});

/** Plain-language explanations of computed metrics. */
export const ASSET_METRIC_GUIDE = Object.freeze({
    readiness: {
        label: 'AI data readiness',
        explain: 'How much of the information FlowSense\'s AI needs is filled in for this asset, weighted by importance. Higher readiness means more reliable predictions.',
        scale: '85%+ AI-ready · 60–84% partial · < 60% needs data',
    },
    health: {
        label: 'Health index',
        explain: 'A 0–100 score combining status, age against design life, maintenance due dates, sensor anomalies and active alerts. Higher is healthier. It is a rule-based summary, not a probability.',
        scale: '80+ good · 60–79 fair · 40–59 poor · < 40 critical',
    },
    life_used: {
        label: 'Design life used',
        explain: 'Age divided by design lifespan. Above 100% the asset is past the life it was designed for and is a replacement candidate.',
    },
    risk: {
        label: 'Failure risk score',
        explain: 'The failure-prediction model\'s 0–100 estimate of how likely the asset is to fail soon, weighted by criticality.',
        scale: '80+ critical · 60–79 high · 40–59 medium · < 40 low',
    },
    predicted_failure: {
        label: 'Predicted failure date',
        explain: 'When the model expects the asset to fail if nothing is done. Scheduling maintenance before this date is the goal.',
    },
    confidence: {
        label: 'Model confidence',
        explain: 'How sure the model is about this prediction. Low confidence usually means missing age, maintenance or sensor data — check the asset\'s AI helper to see what to add.',
        scale: '80%+ high · 60–79% moderate · < 60% low — verify on site',
    },
    est_cost: {
        label: 'Estimated maintenance cost',
        explain: 'The model\'s estimate of the repair cost if the predicted failure is handled now. Waiting for failure usually costs more (emergency call-outs, collateral damage).',
    },
    anomalies: {
        label: 'Sensor anomalies (30 days)',
        explain: 'Readings the anomaly model flagged as unusual in the last 30 days. Repeated anomalies often precede failure.',
    },
});

// ─────────────────────────────────────────────────────────────────────────────
// Normalisation
// ─────────────────────────────────────────────────────────────────────────────
const present = (v) => v !== null && v !== undefined && String(v).trim() !== '';
const num = (v) => (present(v) && Number.isFinite(Number(v)) ? Number(v) : null);
const parseMeta = (m) => {
    if (!m) return {};
    if (typeof m === 'string') { try { return JSON.parse(m) || {}; } catch { return {}; } }
    return typeof m === 'object' ? m : {};
};
const toTime = (d) => {
    if (!present(d)) return null;
    const t = new Date(d).getTime();
    return Number.isFinite(t) ? t : null;
};

export const normalizeAsset = (a = {}) => {
    const meta = parseMeta(a.metadata);
    const family = getAssetFamily(a.type || a.custom_type);
    return {
        id: a.id,
        name: a.name,
        type: a.type === 'Other' && a.custom_type ? a.custom_type : a.type,
        family,
        status: present(a.status) ? String(a.status).toLowerCase().replace(/\s+/g, '_') : null,
        asset_criticality: a.asset_criticality || null,
        managed_by_team_id: present(a.managed_by_team_id) ? a.managed_by_team_id : null,
        location_description: a.location_description,
        latitude: num(a.latitude),
        longitude: num(a.longitude),
        elevation: num(a.elevation),
        base_depth_m: num(meta.base_depth_m),
        rim_elevation_offset_m: num(meta.rim_elevation_offset_m),
        install_date: a.install_date || a.installation_date || a.installed_date || null,
        expected_lifespan_years: num(a.expected_lifespan_years),
        last_maintenance_date: a.last_maintenance_date || null,
        maintenance_interval: num(a.maintenance_interval) || 90,
        manufacturer: a.manufacturer,
        model_number: a.model_number || a.model,
        serial_number: a.serial_number,
        purchase_cost: num(a.purchase_cost ?? a.acquisition_cost),
        warranty_expiration_date: a.warranty_expiration_date || null,
        parent_asset_id: a.parent_asset_id,
        qr_code_tag: a.qr_code_tag,
        model_url: a.model_url,
        anomalies_30d: num(a.anomalous_readings_30d) || 0,
        sensor_count: num(a.sensor_count),
        metadata: meta,
    };
};

const daysBetween = (a, b) => Math.round((b - a) / DAY_MS);

const ACTIVE_ALERT = (al) => ['active', 'new', 'open', 'unacknowledged'].includes(String(al?.status || '').toLowerCase());
const OPEN_WO = (wo) => !['completed', 'closed', 'cancelled'].includes(String(wo?.status || '').toLowerCase());
const BAD_STATUS = {
    failed: 45, offline: 45, critical: 45, error: 45,
    damaged: 30,
    maintenance_required: 15, degraded: 15, warning: 15,
    maintenance: 8, unknown: 10,
};

/** Where can a field be edited in a given context? */
export const fieldLocation = (fieldId, context) => {
    const g = ASSET_FIELD_GUIDE[fieldId];
    if (!g) return null;
    const loc = context === 'form' ? g.form : context === 'edit' ? g.edit : null;
    if (!loc) return null;
    const domField = loc.id || (g.fieldIds ? g.fieldIds[0] : fieldId);
    return { tab: loc.tab, domId: `asset-field-${domField}`, field: domField };
};

// ─────────────────────────────────────────────────────────────────────────────
// Analysis
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {object} asset   any asset shape (API, AssetFormModal or EditAssetModal form data)
 * @param {'form'|'edit'|'view'} context
 * @param {{ sensors?, alerts?, workOrders?, maintenanceLogs?, insights?, prediction?, now? }} extra
 */
export function analyzeAsset(asset, context = 'view', extra = {}) {
    const now = extra.now ?? Date.now();
    const n = normalizeAsset(asset || {});
    const sensors = Array.isArray(extra.sensors) ? extra.sensors : null;
    const alerts = Array.isArray(extra.alerts) ? extra.alerts : [];
    const workOrders = Array.isArray(extra.workOrders) ? extra.workOrders : [];
    const logs = Array.isArray(extra.maintenanceLogs) ? extra.maintenanceLogs : [];
    const sensorCount = sensors ? sensors.length : n.sensor_count;

    const checks = [];
    const add = (id, status, extraFields = {}) => {
        const g = ASSET_FIELD_GUIDE[id];
        if (!g) return;
        if (g.families && !g.families.includes(n.family)) return;
        const level = g.levelFor ? g.levelFor(n.family) : g.level;
        const st = status === true ? 'ok' : status === 'warn' ? 'warning' : status === 'na' ? null : 'missing';
        if (!st) return;
        const loc = fieldLocation(id, context);
        checks.push({ id, ...g, level, weight: level === 'optional' ? Math.min(g.weight, 1) : g.weight, status: st, location: loc, canFix: context === 'view' ? !!(g.form || g.edit) : !!loc, ...extraFields });
    };

    add('name', present(n.name));
    add('type', present(n.type));
    add('status', present(n.status) && n.status !== 'unknown' ? true : present(n.status) ? 'warn' : false,
        n.status === 'unknown' ? { note: 'Status is "unknown" — set the real state so dashboards and alerts are accurate.' } : {});
    add('asset_criticality', present(n.asset_criticality));
    add('managed_by_team_id', present(n.managed_by_team_id));
    add('location_description', present(n.location_description));

    const hasCoords = n.latitude != null && n.longitude != null;
    let coordNote = null;
    if (hasCoords) {
        const inside = n.latitude >= BAHAMAS.minLat && n.latitude <= BAHAMAS.maxLat && n.longitude >= BAHAMAS.minLng && n.longitude <= BAHAMAS.maxLng;
        if (!inside) {
            const swapped = n.longitude >= BAHAMAS.minLat && n.longitude <= BAHAMAS.maxLat && n.latitude >= BAHAMAS.minLng && n.latitude <= BAHAMAS.maxLng;
            const signFlip = n.longitude > 0 && -n.longitude >= BAHAMAS.minLng && -n.longitude <= BAHAMAS.maxLng;
            coordNote = swapped ? 'Latitude and longitude look swapped.'
                : signFlip ? 'Longitude should be negative in The Bahamas (e.g. −77.39).'
                : 'This point is outside The Bahamas — it will distort the map, weather and simulations.';
        }
    }
    add('coordinates', hasCoords && !coordNote ? true : hasCoords ? 'warn' : false, coordNote ? { note: coordNote } : {});
    add('elevation', n.elevation != null ? (n.elevation < -5 || n.elevation > 70 ? 'warn' : true) : false,
        n.elevation != null && (n.elevation < -5 || n.elevation > 70) ? { note: 'Elevation looks implausible for The Bahamas (−5 to 70 m).' }
            : n.elevation == null && hasCoords ? { note: 'Hydraulic simulations assume 2.5 m until this is surveyed.' } : {});
    add('base_depth_m', n.base_depth_m != null && n.base_depth_m >= 0 ? true : n.base_depth_m != null ? 'warn' : false,
        n.base_depth_m != null && n.base_depth_m < 0 ? { note: 'Depth should be a positive number of metres.' } : {});
    add('rim_elevation_offset_m', n.rim_elevation_offset_m != null);

    const installT = toTime(n.install_date);
    add('install_date', installT != null ? (installT > now ? 'warn' : true) : false,
        installT != null && installT > now ? { note: 'Install date is in the future.' } : {});
    add('expected_lifespan_years', n.expected_lifespan_years > 0);
    const lastMaintT = toTime(n.last_maintenance_date);
    add('last_maintenance_date', lastMaintT != null ? (lastMaintT > now ? 'warn' : true) : false,
        lastMaintT != null && lastMaintT > now ? { note: 'Maintenance date is in the future.' } : {});
    add('manufacturer', present(n.manufacturer));
    add('model_number', present(n.model_number));
    add('serial_number', present(n.serial_number));
    add('purchase_cost', n.purchase_cost != null && n.purchase_cost > 0);
    add('warranty_expiration_date', present(n.warranty_expiration_date));
    if (sensorCount != null) add('sensors', sensorCount > 0);
    add('parent_asset_id', present(n.parent_asset_id));
    add('qr_code_tag', present(n.qr_code_tag));
    add('model_url', present(n.model_url));

    // Weighted score (warnings count half)
    let total = 0, got = 0;
    for (const c of checks) {
        total += c.weight;
        if (c.status === 'ok') got += c.weight;
        else if (c.status === 'warning') got += c.weight * 0.5;
    }
    const score = total ? Math.round((got / total) * 100) : 0;
    const grade = score >= 85 ? { label: 'AI-ready', color: 'green' } : score >= 60 ? { label: 'Partially ready', color: 'yellow' } : { label: 'Needs data', color: 'red' };

    // Per-model readiness
    const models = Object.entries(ASSET_AI_MODELS).map(([key, model]) => {
        const relevant = checks.filter(c => c.usedBy.includes(key));
        if (!relevant.length) return null;
        if (key === 'hydraulic' && !ASSET_FAMILIES[n.family].hydraulic) return null;
        const blocking = relevant.filter(c => c.status === 'missing' && (c.level === 'required' || c.weight >= 3));
        const okCount = relevant.filter(c => c.status === 'ok').length;
        const state = blocking.length ? 'blocked' : okCount === relevant.length ? 'ready' : 'partial';
        return { key, ...model, state, relevant, missing: relevant.filter(c => c.status !== 'ok') };
    }).filter(Boolean);

    // ── Health index + drivers ──────────────────────────────────────────────
    const family = ASSET_FAMILIES[n.family];
    const lifespan = n.expected_lifespan_years > 0 ? n.expected_lifespan_years : family.lifespan;
    const ageYears = installT != null && installT <= now ? (now - installT) / YEAR_MS : null;
    const lifeUsed = ageYears != null ? ageYears / lifespan : null;
    const remainingYears = ageYears != null ? Math.max(0, lifespan - ageYears) : null;
    const nextMaintT = lastMaintT != null ? lastMaintT + n.maintenance_interval * DAY_MS : null;
    const maintOverdueDays = nextMaintT != null ? Math.floor((now - nextMaintT) / DAY_MS) : null;
    const activeAlerts = alerts.filter(ACTIVE_ALERT);
    const criticalAlerts = activeAlerts.filter(a => String(a.severity).toLowerCase() === 'critical');
    const openWOs = workOrders.filter(OPEN_WO);

    const drivers = [];
    let health = 100;
    const hit = (pts, label, detail) => { if (pts <= 0) return; health -= pts; drivers.push({ points: pts, label, detail }); };
    if (n.status && BAD_STATUS[n.status]) hit(BAD_STATUS[n.status], `Status: ${n.status.replace(/_/g, ' ')}`, 'Current operating state reported for the asset.');
    if (lifeUsed != null) {
        if (lifeUsed >= 1) hit(25, `${Math.round(lifeUsed * 100)}% of design life used`, `About ${ageYears.toFixed(1)} of ${lifespan} years — past design life.`);
        else if (lifeUsed >= 0.8) hit(12, `${Math.round(lifeUsed * 100)}% of design life used`, 'Approaching end of design life.');
    }
    if (maintOverdueDays != null && maintOverdueDays > 0) {
        hit(maintOverdueDays > n.maintenance_interval ? 15 : 8, `Maintenance ${maintOverdueDays} days overdue`, `Service interval is ${n.maintenance_interval} days.`);
    } else if (lastMaintT == null) {
        hit(5, 'No maintenance on record', 'Unknown service history adds uncertainty.');
    }
    if (n.anomalies_30d > 0) hit(Math.min(20, n.anomalies_30d * 4), `${n.anomalies_30d} sensor anomal${n.anomalies_30d === 1 ? 'y' : 'ies'} (30 d)`, 'Unusual readings flagged by the anomaly model.');
    if (activeAlerts.length) hit(Math.min(15, activeAlerts.length * 5 + criticalAlerts.length * 3), `${activeAlerts.length} active alert${activeAlerts.length === 1 ? '' : 's'}`, criticalAlerts.length ? `${criticalAlerts.length} critical.` : 'Unresolved alerts on this asset.');
    health = Math.max(0, Math.min(100, Math.round(health)));
    const healthLevel = health >= 80 ? { label: 'Good', color: 'green' } : health >= 60 ? { label: 'Fair', color: 'yellow' } : health >= 40 ? { label: 'Poor', color: 'orange' } : { label: 'Critical', color: 'red' };
    drivers.sort((a, b) => b.points - a.points);

    // Model output (Predictive Maintenance payload or asset ML fields), read defensively
    const p = extra.prediction || {};
    const riskRaw = num(p.riskScore ?? p.risk_score ?? asset?.risk_score ?? asset?.failure_risk_score);
    const confRaw = num(p.confidence ?? asset?.prediction_confidence);
    const model = {
        riskScore: riskRaw != null ? Math.round(riskRaw <= 1 ? riskRaw * 100 : riskRaw) : null,
        predictedFailureDate: p.predictedFailureDate || p.predicted_failure_date || asset?.predicted_failure_date || null,
        confidence: confRaw != null ? Math.round(confRaw <= 1 ? confRaw * 100 : confRaw) : null,
        estimatedCost: num(p.estimatedMaintenanceCost ?? p.estimated_cost),
    };
    const hasModel = model.riskScore != null || model.predictedFailureDate != null;

    // Lifecycle: repair spend vs purchase cost
    const repairSpend = logs.reduce((s, l) => s + (num(l.cost) || 0), 0);
    const repairRatio = n.purchase_cost > 0 && repairSpend > 0 ? repairSpend / n.purchase_cost : null;

    // ── Insights ───────────────────────────────────────────────────────────
    const insights = [];
    if (hasModel && model.riskScore != null) {
        const days = model.predictedFailureDate ? Math.ceil((toTime(model.predictedFailureDate) - now) / DAY_MS) : null;
        insights.push({
            severity: model.riskScore >= 80 ? 'critical' : model.riskScore >= 60 ? 'warning' : 'info',
            title: `Failure risk ${model.riskScore}/100${days != null ? ` · predicted in ${days} days` : ''}`,
            text: model.riskScore >= 60 ? 'Schedule preventive maintenance before the predicted date — it is far cheaper than an emergency repair.' : 'Low predicted risk. Keep the maintenance schedule so the prediction stays accurate.',
        });
    }
    // Insights from the ML service / live events come first — they are the freshest signal
    (Array.isArray(extra.insights) ? extra.insights : []).slice(0, 3).forEach(ins => insights.push({
        severity: String(ins.severity || ins.type || 'info').toLowerCase(), title: ins.title || 'AI insight', text: ins.message || '', fromModel: true,
    }));
    if (lifeUsed != null && lifeUsed >= 1) insights.push({ severity: 'warning', title: 'Past design life', text: `In service ${ageYears.toFixed(1)} years against a ${lifespan}-year design life. Add it to the replacement plan and watch for rising repair costs.` });
    else if (lifeUsed != null && lifeUsed >= 0.8) insights.push({ severity: 'info', title: `${Math.round(remainingYears)} years of design life left`, text: 'Start budgeting for replacement and increase inspection frequency.' });
    if (maintOverdueDays != null && maintOverdueDays > 0) insights.push({ severity: maintOverdueDays > n.maintenance_interval ? 'critical' : 'warning', title: `Maintenance overdue by ${maintOverdueDays} days`, text: 'Overdue service is one of the strongest failure signals. Log the next service as soon as it is done so the model learns from it.' });
    if (repairRatio != null && repairRatio >= 0.5) insights.push({ severity: 'warning', title: `Repairs have cost ${Math.round(repairRatio * 100)}% of the purchase price`, text: 'Repair-vs-replace suggests evaluating replacement — continued repairs may cost more than a new unit.' });
    if (n.anomalies_30d > 0 && ['operational', 'online', 'active'].includes(n.status)) insights.push({ severity: 'warning', title: 'Anomalies on an "operational" asset', text: `${n.anomalies_30d} unusual reading${n.anomalies_30d === 1 ? '' : 's'} in 30 days. Inspect before it fails.` });
    if (criticalAlerts.length && ['operational', 'online', 'active'].includes(n.status)) insights.push({ severity: 'critical', title: 'Critical alert but status is operational', text: 'Check the asset and update its status so dashboards and dispatch reflect reality.' });
    if (family.sensors && sensorCount === 0) insights.push({ severity: 'info', title: 'No live sensors', text: `${family.label}s benefit most from sensors — anomaly detection can warn before failure. Link a sensor in Sensor Management.` });
    if (n.elevation != null && ELEVATION_FAMILIES.includes(n.family)) {
        const effective = n.elevation - SEA_LEVEL_RISE_M;
        if (effective < 1.8) insights.push({ severity: 'warning', title: 'Low-lying — storm-surge exposure', text: `At ${n.elevation} m it is likely to be flooded in a hurricane surge. Prioritise protection and include it in pre-storm plans.` });
    }
    if (DEPTH_FAMILIES.includes(n.family) && n.base_depth_m == null) insights.push({ severity: 'info', title: 'Design depth not calibrated', text: 'The hydraulic model can\'t estimate storage or surcharge time without the sump / well depth.' });
    const warrantyT = toTime(n.warranty_expiration_date);
    if (warrantyT != null && warrantyT > now && warrantyT - now < 90 * DAY_MS) insights.push({ severity: 'info', title: `Warranty ends in ${daysBetween(now, warrantyT)} days`, text: 'Inspect now and claim any defects while still covered.' });
    if (warrantyT != null && warrantyT > now && (activeAlerts.length || ['failed', 'damaged', 'offline'].includes(n.status))) insights.push({ severity: 'info', title: 'Still under warranty', text: 'This fault may be claimable from the manufacturer.' });
    if (['critical', 'high'].includes(String(n.asset_criticality).toLowerCase()) && !present(n.managed_by_team_id)) insights.push({ severity: 'info', title: 'High-criticality asset without a team', text: 'Assign a responsible team so AI-generated work orders are routed immediately.' });
    if (openWOs.length) insights.push({ severity: 'info', title: `${openWOs.length} open work order${openWOs.length === 1 ? '' : 's'}`, text: 'Close completed work orders and log the maintenance so the failure model learns from the repair.' });

    // ── Recommended actions ────────────────────────────────────────────────
    const actions = [];
    const push = (a) => { if (!actions.some(x => x.label === a.label)) actions.push(a); };
    if (hasModel && model.riskScore >= 60) push({ intent: 'SCHEDULE', label: 'Schedule preventive maintenance', priority: model.riskScore >= 80 ? 'critical' : 'high', explain: 'Predicted failure risk is high.' });
    if (['failed', 'offline', 'damaged', 'critical'].includes(n.status)) push({ intent: 'REPORT_FAILURE', label: 'Report / dispatch repair', priority: 'critical', explain: `Asset is ${n.status.replace(/_/g, ' ')}.` });
    if (maintOverdueDays != null && maintOverdueDays > 0) push({ intent: 'LOG_MAINTENANCE', label: 'Service it and log the maintenance', priority: maintOverdueDays > n.maintenance_interval ? 'high' : 'medium', explain: `${maintOverdueDays} days overdue.` });
    if (lifeUsed != null && lifeUsed >= 1) push({ intent: 'VIEW', label: 'Review for replacement', priority: 'medium', explain: 'Past design life.' });
    const topMissing = checks.filter(c => c.status !== 'ok' && c.level !== 'optional').sort((a, b) => b.weight - a.weight)[0];
    if (topMissing) push({ intent: 'EDIT', label: `Add ${topMissing.label.toLowerCase()}`, priority: topMissing.level === 'required' ? 'high' : 'low', explain: topMissing.why.split('.')[0] + '.', fieldId: topMissing.id });
    if (!actions.length) push({ intent: 'VIEW', label: 'No action needed — keep routine maintenance', priority: 'low', explain: 'Healthy and well documented.' });

    const missingRequired = checks.filter(c => c.status === 'missing' && c.level === 'required');
    const nextBest = checks.filter(c => c.status !== 'ok').sort((a, b) => b.weight - a.weight)[0] || null;
    const summary = missingRequired.length
        ? `Add ${missingRequired.map(c => c.label.toLowerCase()).join(' and ')} to make this a usable record.`
        : nextBest
            ? `Next most valuable: ${nextBest.label.toLowerCase()} — ${nextBest.why.split('.')[0].toLowerCase()}.`
            : 'Everything FlowSense\'s AI uses for this asset is filled in.';

    return {
        score, grade, checks, models, insights, actions, missingRequired, summary,
        family: n.family, familyLabel: family.label,
        health, healthLevel, drivers,
        ageYears, lifespan, lifeUsed, remainingYears,
        nextMaintenance: nextMaintT != null ? new Date(nextMaintT) : null, maintOverdueDays,
        activeAlerts: activeAlerts.length, openWorkOrders: openWOs.length, sensorCount,
        repairSpend, repairRatio,
        model, hasModel,
        normalized: n,
    };
}

// Memo per asset object (tables / panels call this for many rows)
const cache = new WeakMap();
export const getAssetAnalysis = (asset, extra) => {
    if (!asset || typeof asset !== 'object' || extra) return analyzeAsset(asset, 'view', extra || {});
    const hit = cache.get(asset);
    if (hit) return hit;
    const v = analyzeAsset(asset, 'view');
    cache.set(asset, v);
    return v;
};

/** Network roll-up for dashboards. sensorsByAsset: Map<assetId, sensor[]> (optional). */
export function summarizeAssets(assets = [], { sensorsByAsset } = {}) {
    const byFamily = {};
    const blocked = {};
    const missingCounts = {};
    let readinessSum = 0, aiReady = 0, poorHealth = 0;
    const rows = [];
    (Array.isArray(assets) ? assets : []).forEach(a => {
        const sensors = sensorsByAsset ? (sensorsByAsset.get(a.id) || []) : undefined;
        const r = sensors ? analyzeAsset(a, 'view', { sensors }) : getAssetAnalysis(a);
        readinessSum += r.score;
        if (r.score >= 85) aiReady += 1;
        if (r.health < 60) poorHealth += 1;
        const f = (byFamily[r.family] ||= { family: r.family, label: r.familyLabel, count: 0, readinessSum: 0, missing: {} });
        f.count += 1; f.readinessSum += r.score;
        r.checks.forEach(c => {
            if (c.status === 'missing' && c.level !== 'optional') {
                f.missing[c.id] = (f.missing[c.id] || 0) + 1;
                missingCounts[c.id] = (missingCounts[c.id] || 0) + 1;
            }
        });
        r.models.forEach(m => { (blocked[m.key] ||= { ready: 0, partial: 0, blocked: 0 })[m.state] += 1; });
        rows.push({ asset: a, analysis: r });
    });
    const total = rows.length;
    const families = Object.values(byFamily).map(f => {
        const topMissing = Object.entries(f.missing).sort((a, b) => b[1] - a[1])[0];
        return {
            family: f.family, label: f.label, count: f.count,
            readiness: Math.round(f.readinessSum / f.count),
            topMissing: topMissing ? { id: topMissing[0], label: ASSET_FIELD_GUIDE[topMissing[0]].label, count: topMissing[1] } : null,
        };
    }).sort((a, b) => b.count - a.count);
    const critRank = (a) => ({ critical: 0, high: 1, medium: 2, low: 3 }[String(a.asset_criticality || '').toLowerCase()] ?? 4);
    const gaps = rows.filter(r => r.analysis.score < 85)
        .sort((x, y) => critRank(x.asset) - critRank(y.asset) || x.analysis.score - y.analysis.score)
        .slice(0, 8);
    const atRisk = [...rows].sort((x, y) => x.analysis.health - y.analysis.health).filter(r => r.analysis.health < 80).slice(0, 8);
    const topMissing = Object.entries(missingCounts).sort((a, b) => b[1] - a[1]).slice(0, 5)
        .map(([id, count]) => ({ id, label: ASSET_FIELD_GUIDE[id].label, count, usedBy: ASSET_FIELD_GUIDE[id].usedBy }));
    return {
        total, aiReady, poorHealth,
        readinessAvg: total ? Math.round(readinessSum / total) : 0,
        families, models: blocked, gaps, atRisk, topMissing,
    };
}

/** Insights from useAIInsights / aiInsightsService that reference an asset. */
export const insightAssetId = (ins) => {
    if (!ins) return undefined;
    const v = ins.asset_id ?? ins.assetId ?? ins.actionPayload?.assetId ?? ins.actionPayload?.asset_id ?? ins.metadata?.asset_id;
    return v === null ? undefined : v;
};
export const groupInsightsByAsset = (insights) => {
    const map = new Map();
    (Array.isArray(insights) ? insights : []).forEach(ins => {
        const id = insightAssetId(ins);
        if (id === undefined) return;
        const k = String(id);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(ins);
    });
    return map;
};

/** Plain-language explanation of a Predictive Maintenance row. */
export function explainPrediction(pred = {}, now = Date.now()) {
    const risk = num(pred.riskScore) ?? 0;
    const conf = num(pred.confidence);
    const t = toTime(pred.predictedFailureDate);
    const days = t != null ? Math.ceil((t - now) / DAY_MS) : null;
    const level = risk >= 80 ? { label: 'Critical', color: 'red' } : risk >= 60 ? { label: 'High', color: 'orange' } : risk >= 40 ? { label: 'Medium', color: 'yellow' } : { label: 'Low', color: 'green' };
    const family = getAssetFamily(pred.assetType);
    const points = [];
    points.push(`Risk ${risk}/100 is ${level.label.toLowerCase()} — ${risk >= 80 ? 'failure is likely soon' : risk >= 60 ? 'failure is likely within the planning window' : risk >= 40 ? 'worth monitoring' : 'no near-term concern'}.`);
    if (days != null) points.push(days < 0 ? `The predicted failure date passed ${Math.abs(days)} days ago — inspect immediately and log the result so the model can learn.` : `Predicted failure in ${days} day${days === 1 ? '' : 's'}${days <= 7 ? ' — act this week' : days <= 30 ? ' — schedule this month' : ''}.`);
    if (conf != null) points.push(conf >= 80 ? `Confidence ${conf}% — the model has good data for this asset.` : conf >= 60 ? `Confidence ${conf}% — moderate; adding missing install / maintenance data will sharpen it.` : `Confidence ${conf}% — low. Verify on site; the asset is probably missing age, maintenance or sensor data.`);
    if (pred.estimatedMaintenanceCost) points.push(`Planned repair now is estimated at $${Math.round(pred.estimatedMaintenanceCost).toLocaleString()} — emergency repair after failure usually costs more.`);
    const action = risk >= 80 || (days != null && days <= 7) ? 'Schedule maintenance now and assign a crew.'
        : risk >= 60 ? 'Schedule preventive maintenance before the predicted date.'
        : risk >= 40 ? 'Add to the next inspection round.'
        : 'No action needed — keep routine maintenance.';
    return { level, days, points, action, familyLabel: ASSET_FAMILIES[family].label, usesSensors: ASSET_FAMILIES[family].sensors };
}

// ─────────────────────────────────────────────────────────────────────────────
// Failure triage (Report Failure modal)
// Rule-based: keywords in the description + asset type + current health/insights.
// It suggests — the reporter always decides.
// ─────────────────────────────────────────────────────────────────────────────
export const FAILURE_CHECKS = Object.freeze({
    well: ['Check pump run status and discharge pressure', 'Look for silt / sand in the discharge', 'Inspect the casing and screen for collapse', 'Verify the level sensor reading against a dip measurement'],
    basin: ['Clear debris from the grate and sump', 'Check the outlet pipe for blockage', 'Look for cracked walls or a sunken frame', 'Note standing water depth and how fast it drains'],
    pump: ['Listen for bearing noise and feel for vibration', 'Check the motor current and breaker', 'Inspect seals for leaks', 'Confirm suction is not air-locked or blocked'],
    hydrant: ['Check for leaks at the base and nozzles', 'Try the operating nut — seized or loose?', 'Look for impact damage', 'Confirm the isolation valve position'],
    valve: ['Is it seized, leaking or passing?', 'Check the valve box is accessible and not paved over', 'Record the number of turns to open / close'],
    pipe: ['Mark the leak / break location precisely', 'Estimate flow and whether the road is undermined', 'Identify the nearest isolation valves'],
    tank: ['Check the level reading against a visual', 'Inspect for leaks, corrosion and overflow', 'Verify inlet / outlet valves'],
    meter: ['Check power and communications', 'Compare the reading with a manual reading', 'Inspect for tampering or water ingress'],
    electrical: ['Check power supply, fuse and breaker', 'Look for exposed wiring — make safe first', 'Note whether nearby units are also out'],
    road: ['Measure the defect (length × width × depth)', 'Is it a traffic hazard right now? Cone it off', 'Note drainage or water involvement'],
    bridge: ['Close to traffic if structural damage is suspected', 'Photograph cracks, spalling and bearings', 'Request an engineer inspection'],
    other: ['Describe what is not working and since when', 'Photograph the fault', 'Note any safety hazard'],
});

const TRIAGE_KEYWORDS = [
    { re: /(fire|smok|spark|explo|electrocut|live wire|gas)/i, severity: 'critical', category: 'electrical', reason: 'Fire / electrical hazard mentioned' },
    { re: /(flood|overflow|surcharg|sinkhole|collaps|undermin|washout)/i, severity: 'critical', category: 'structural', reason: 'Flooding or collapse risk mentioned' },
    { re: /(burst|main break|gushing|no water|outage|total loss|not working at all)/i, severity: 'high', category: 'mechanical', reason: 'Loss of service mentioned' },
    { re: /(leak|drip|seep|pressure drop|low pressure)/i, severity: 'medium', category: 'mechanical', reason: 'Leak / pressure problem mentioned' },
    { re: /(vibrat|noise|grind|bearing|overheat|seiz|stuck|jam)/i, severity: 'medium', category: 'mechanical', reason: 'Mechanical wear symptoms mentioned' },
    { re: /(crack|broken|damag|impact|hit by|pothole|spall|corro|rust)/i, severity: 'medium', category: 'structural', reason: 'Physical damage mentioned' },
    { re: /(power|breaker|fuse|wiring|electric|voltage|trip)/i, severity: 'medium', category: 'electrical', reason: 'Electrical fault mentioned' },
    { re: /(sensor|reading|offline|no data|scada|telemetry|signal|software|app)/i, severity: 'low', category: 'software', reason: 'Data / communications issue mentioned' },
    { re: /(block|debris|clog|silt|sand|trash)/i, severity: 'medium', category: 'mechanical', reason: 'Blockage mentioned' },
];
const SEV_RANK = { low: 0, medium: 1, high: 2, critical: 3 };
const RANK_SEV = ['low', 'medium', 'high', 'critical'];

/**
 * @returns {{ severity, category, urgent, reasons: string[], confidence: 'low'|'medium'|'high', checks: string[], family, familyLabel }}
 */
export function triageFailure({ description = '', asset = {}, analysis = null, insights = [] } = {}) {
    const a = analysis || (asset && Object.keys(asset).length ? getAssetAnalysis(asset) : null);
    const family = a ? a.family : getAssetFamily(asset?.type);
    const reasons = [];
    let category = null;
    let hits = 0;
    let kwRank = -1;
    TRIAGE_KEYWORDS.forEach(k => {
        if (k.re.test(description)) {
            hits += 1;
            reasons.push(k.reason);
            kwRank = Math.max(kwRank, SEV_RANK[k.severity]);
            if (!category) category = k.category;
        }
    });
    let rank = kwRank >= 0 ? kwRank : 1; // medium when no symptoms recognised
    const crit = String(asset?.asset_criticality || '').toLowerCase();
    if (crit === 'critical') { rank = Math.min(3, rank + 1); reasons.push('Asset criticality is critical'); }
    else if (crit === 'high' && rank < 2) { rank = 2; reasons.push('High-criticality asset'); }
    if (a && a.health < 40) { rank = Math.max(rank, 2); reasons.push(`Health index already ${a.health}/100`); }
    const liveCritical = (insights || []).some(i => String(i.severity || i.type).toLowerCase() === 'critical');
    if (liveCritical) { rank = Math.max(rank, 2); reasons.push('A critical AI alert is open on this asset'); }
    if (a && a.model?.riskScore >= 80) { rank = Math.max(rank, 2); reasons.push(`Failure model risk ${a.model.riskScore}/100`); }
    if (!category) category = ['electrical', 'meter'].includes(family) ? 'electrical' : ['road', 'bridge', 'basin'].includes(family) ? 'structural' : 'mechanical';
    const severity = RANK_SEV[rank];
    const confidence = hits >= 2 || (hits && reasons.length >= 2) ? 'high' : hits ? 'medium' : 'low';
    return {
        severity, category, urgent: rank >= 2, reasons: reasons.length ? reasons : ['No specific symptoms recognised yet — add more detail for a better suggestion'],
        confidence, checks: FAILURE_CHECKS[family] || FAILURE_CHECKS.other, family, familyLabel: ASSET_FAMILIES[family].label,
    };
}